/**
 * Vue ↔ Livewire bridge.
 *
 * Registers Vue islands that live inside Livewire components and removes the
 * plumbing every island used to repeat by hand:
 *
 *   - reads the `data-config` seeded by the server into a reactive `this.config`,
 *   - keeps `this.config` in sync after every Livewire round-trip,
 *   - exposes `this.call('action', ...args)` to invoke Livewire actions and
 *     flips `this.processing` on/off around the trip,
 *   - exposes `this.callQuiet('action', ...args)` for fire-and-forget actions:
 *     no `processing`, no awaiting; pair it with a `#[Renderless]` action on the
 *     server so nothing re-renders and the optimistic Vue state is kept,
 *   - exposes `this.wire()` for direct access to the Livewire component,
 *   - lets the server invoke a component method via `$this->callVue('method')`,
 *   - mounts/unmounts instances automatically, including after Livewire
 *     navigation and DOM updates.
 *
 * A component is just its markup and logic:
 *
 *   VueBridge.component('media-matrix', {
 *       template: `<div>{{ config.title }}</div>`,
 *       methods: {
 *           save() { this.call('save', this.config.title); },
 *       },
 *   });
 */
window.VueBridge = (function () {
    const definitions = {};
    const instances = new Map();

    function tagFor(name) {
        return 'vue-' + name;
    }

    function readConfig(host) {
        const raw = host.getAttribute('data-config');

        try {
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            console.error('[VueBridge] invalid data-config on <' + host.tagName.toLowerCase() + '>', e);

            return {};
        }
    }

    function buildApp(name, definition, host) {
        const userData = definition.data;
        const userMounted = definition.mounted;
        const userUnmounted = definition.unmounted;
        const initialConfig = readConfig(host);
        const wireId = host.getAttribute('wire:id');

        const bridgeMethods = {
            wire() {
                return window.Livewire.find(this.bridgeWireId);
            },
            call(method, ...args) {
                this.processing = true;

                return Promise.resolve(window.Livewire.find(this.bridgeWireId).call(method, ...args))
                    .finally(() => this.processing = false);
            },
            callQuiet(method, ...args) {
                return window.Livewire.find(this.bridgeWireId).call(method, ...args);
            },
        };

        return Object.assign({}, definition, {
            data() {
                const base = userData ? userData.call(this) : {};

                return Object.assign({config: initialConfig, processing: false}, base);
            },
            methods: Object.assign({}, bridgeMethods, definition.methods),
            mounted() {
                this.bridgeHost = host;
                this.bridgeWireId = wireId;

                this.bridgeListener = (event) => {
                    const {type, payload} = event.detail;

                    if (type === 'config') {
                        this.config = payload;
                        this.processing = false;
                    } else if (type === 'call') {
                        const fn = this[payload.method];

                        if (typeof fn === 'function') {
                            fn.apply(this, payload.arguments || []);
                        } else {
                            console.warn('[VueBridge] ' + name + ': no method "' + payload.method + '"');
                        }
                    }
                };

                window.addEventListener('vue-bridge:' + wireId, this.bridgeListener);

                if (userMounted) {
                    userMounted.call(this);
                }
            },
            unmounted() {
                if (this.bridgeListener) {
                    window.removeEventListener('vue-bridge:' + wireId, this.bridgeListener);
                }

                if (userUnmounted) {
                    userUnmounted.call(this);
                }
            },
        });
    }

    function mount(name, host) {
        if (instances.has(host) || !host.getAttribute('wire:id')) {
            return;
        }

        const app = window.Vue.createApp(buildApp(name, definitions[name], host));
        instances.set(host, app);
        app.mount(host);
    }

    function unmountRemoved() {
        instances.forEach((app, host) => {
            if (!host.isConnected) {
                app.unmount();
                instances.delete(host);
            }
        });
    }

    function unmountAll() {
        instances.forEach((app) => app.unmount());
        instances.clear();
    }

    function scan(name) {
        unmountRemoved();

        const names = name ? [name] : Object.keys(definitions);

        names.forEach((current) => {
            document.querySelectorAll(tagFor(current)).forEach((host) => mount(current, host));
        });
    }

    function component(name, definition) {
        definitions[name] = definition;

        if (document.readyState !== 'loading') {
            scan(name);
        }
    }

    window.addEventListener('load', () => scan());
    document.addEventListener('livewire:navigating', () => unmountAll());
    document.addEventListener('livewire:navigated', () => scan());
    document.addEventListener('livewire:init', () => {
        window.Livewire.hook('commit', ({succeed}) => succeed(() => scan()));
    });

    return {component, scan};
})();
